import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowDownAZ,
  ArrowUpAZ,
  BadgeCheck,
  BrainCircuit,
  CalendarDays,
  ChevronLeft,
  Database,
  Eye,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  Shuffle,
  UserRound,
  Users,
} from "lucide-react";
import { useCloudAccount } from "@/components/account/CloudAccountGate";
import {
  claimAdminOwner,
  getAdminParticipant,
  getAdminSession,
  listAdminParticipants,
} from "@/components/admin/adminApi";
import {
  BEHAVIORS,
  PREFERRED_ACTIVITY_OPTIONS,
  SECTIONS,
} from "@/components/assessment/assessmentData";
import { computeDimensions } from "@/components/assessment/assessmentScoring";
import { sexLabel } from "@/components/assessment/participantIdentity";
import AdminAssessmentPdfButton from "@/components/admin/AdminAssessmentPdfButton";
import QuickAssessmentResults from "@/components/admin/assessment/QuickAssessmentResults";
import {
  canOpenQuickAssessmentResults,
  normalizeAdminPathname,
} from "@/components/admin/assessment/quickAssessmentAccess";
import IntelligenceLab from "@/components/admin/intelligence/IntelligenceLab";
import { isIntelligenceLabEnabled } from "@/components/admin/intelligence/availability";

const intelligenceLabEnabled = isIntelligenceLabEnabled(
  process.env.REACT_APP_APOS_INTELLIGENCE_LAB,
);

const intelligenceAgentEnabled =
  process.env.REACT_APP_APOS_INTELLIGENCE_AGENT === "true";

const sortOptions = [
  ["accountCreatedAt", "Date added"],
  ["fullName", "Name"],
  ["email", "Email"],
  ["lastActiveAt", "Last active"],
  ["latestAssessmentAt", "Latest assessment"],
  ["assessmentCount", "Assessment count"],
  ["activeProfileTitle", "Adaptive Profile"],
];

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(includeTime ? { hour: "numeric", minute: "2-digit" } : {}),
  }).format(date);
};

const numericScore = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(1) : "—";

const assessmentTime = (assessment) => {
  const value = new Date(assessment?.completedAt || assessment?.updatedAt || 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

const activityLabel = (key) =>
  PREFERRED_ACTIVITY_OPTIONS.find((option) => option.key === key)?.label || key;

const behaviourLabel = (key) =>
  BEHAVIORS.find((option) => option.key === key)?.label || key;

const responseLabel = (value) => {
  const number = Number(value);
  if (number === 1) return "Minimal improvement";
  if (number === 2) return "Slight improvement";
  if (number === 3) return "Moderate improvement";
  if (number === 4) return "Good improvement";
  if (number === 5) return "Great improvement";
  return "Not rated";
};

const StatCard = ({ icon: Icon, label, value, detail }) => (
  <div
    className="rounded-[24px] p-5 forr-surface"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.17em] forr-text-mute">
          {label}
        </div>
        <div className="mt-2 font-display text-3xl forr-text-ink">{value}</div>
        {detail && <p className="mt-2 text-xs forr-text-soft">{detail}</p>}
      </div>
      <div
        className="flex h-10 w-10 items-center justify-center rounded-2xl"
        style={{ backgroundColor: "var(--forr-teal-tint)" }}
      >
        <Icon size={19} className="forr-text-teal-deep" />
      </div>
    </div>
  </div>
);

const AdminHeader = ({ account, onSignOut }) => {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const signOut = async () => {
    setSigningOut(true);
    const result = await onSignOut();
    setSigningOut(false);
    if (result?.ok) navigate("/app", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-50 border-b backdrop-blur-xl"
      style={{
        backgroundColor: "rgba(247,249,247,0.95)",
        borderColor: "var(--forr-line-soft)",
      }}
    >
      <div className="mx-auto flex min-h-[72px] max-w-[1240px] items-center justify-between gap-4 px-4 sm:px-7">
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className="flex min-w-0 items-center gap-3 text-left"
        >
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[17px]"
            style={{
              background:
                "linear-gradient(145deg, var(--forr-teal-deep), var(--forr-teal))",
            }}
          >
            <ShieldCheck size={21} color="#fff" />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-display text-lg forr-text-ink">
              APOS Admin
            </span>
            <span className="mt-0.5 block truncate text-[10px] uppercase tracking-[0.18em] forr-text-mute">
              Private developer dashboard
            </span>
          </span>
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="hidden rounded-full px-4 py-2 text-xs font-medium forr-btn-secondary sm:inline-flex"
          >
            Open Companion
          </button>
          <div className="hidden max-w-[220px] truncate text-xs forr-text-mute md:block">
            {account?.email}
          </div>
          <button
            type="button"
            onClick={signOut}
            disabled={signingOut}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full forr-surface"
            style={{ border: "1px solid var(--forr-line-soft)" }}
            aria-label="Sign out"
            title="Sign out"
          >
            {signingOut ? (
              <LoaderCircle size={16} className="animate-spin" />
            ) : (
              <LogOut size={16} className="forr-text-teal-deep" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

const LoadingPanel = ({ label = "Opening private dashboard" }) => (
  <div className="flex min-h-[48vh] items-center justify-center text-center">
    <div>
      <LoaderCircle size={30} className="mx-auto animate-spin forr-text-teal-deep" />
      <p className="mt-4 text-sm forr-text-mute">{label}</p>
    </div>
  </div>
);

const AccessPanel = ({ session, busy, error, onActivate }) => {
  const navigate = useNavigate();
  const eligible = Boolean(session?.admin?.bootstrapEligible);
  return (
    <div className="mx-auto max-w-2xl py-14 sm:py-24">
      <div
        className="rounded-[30px] p-7 forr-surface sm:p-10"
        style={{ border: "1px solid var(--forr-line)" }}
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-[20px]"
          style={{ backgroundColor: "var(--forr-teal-tint)" }}
        >
          <LockKeyhole size={26} className="forr-text-teal-deep" />
        </div>
        <div className="mt-6 forr-overline">Private access</div>
        <h1 className="mt-3 font-display text-4xl tracking-[-0.03em] forr-text-ink">
          {eligible ? "Establish the owner dashboard." : "This account is not authorized."}
        </h1>
        <p className="mt-5 text-base leading-relaxed forr-text-soft">
          {eligible
            ? "This is the oldest APOS account currently indexed and no owner has been established. Activate it once to make this signed-in account the private, read-only owner account."
            : "The participant account is working, but it does not have permission to view the APOS participant directory or assessment records."}
        </p>
        {eligible && (
          <div className="mt-6 rounded-2xl p-4 forr-panel-information">
            <div className="text-sm font-medium forr-text-ink">
              What owner access contains
            </div>
            <p className="mt-2 text-xs leading-relaxed forr-text-soft">
              Participant names, emails, account dates, assessment history, check-in history,
              calendar records, and progress metadata. The dashboard is read-only and records
              participant-profile access in an audit log.
            </p>
          </div>
        )}
        {error && (
          <p className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-800" role="alert">
            {error}
          </p>
        )}
        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          {eligible && (
            <button
              type="button"
              onClick={onActivate}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold forr-btn-primary disabled:opacity-50"
            >
              {busy ? <LoaderCircle size={17} className="animate-spin" /> : <BadgeCheck size={17} />}
              Activate owner dashboard
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-medium forr-btn-secondary"
          >
            Return to Companion
          </button>
        </div>
      </div>
    </div>
  );
};

const Directory = ({ onOpen, onOpenIntelligence, onOpenQuickResults }) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("accountCreatedAt");
  const [direction, setDirection] = useState("desc");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const next = await listAdminParticipants({ query, sort, direction });
        if (!cancelled) setResult(next);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query ? 250 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [direction, query, refreshKey, sort]);

  const stats = result?.stats || {};
  const participants = result?.participants || [];

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="forr-overline">Participant registry</div>
          <h1 className="mt-2 font-display text-4xl tracking-[-0.035em] forr-text-ink sm:text-5xl">
            Accounts and Adaptive Profiles
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed forr-text-soft sm:text-base">
            Search participant accounts, review every saved assessment, and inspect longitudinal
            check-in and calendar history. This dashboard is private and read-only.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {onOpenQuickResults && (
            <button
              type="button"
              onClick={onOpenQuickResults}
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold forr-btn-secondary"
            >
              <Shuffle size={16} /> Quick assessment results
            </button>
          )}
          {intelligenceLabEnabled && (
            <button
              type="button"
              onClick={onOpenIntelligence}
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold forr-btn-primary"
            >
              <BrainCircuit size={16} /> Open Intelligence Lab
            </button>
          )}
          <button
            type="button"
            onClick={() => setRefreshKey((value) => value + 1)}
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary"
          >
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard icon={Users} label="Total accounts" value={stats.totalAccounts ?? "—"} />
        <StatCard icon={UserRound} label="Added in 7 days" value={stats.addedLast7Days ?? "—"} />
        <StatCard icon={Activity} label="Active in 7 days" value={stats.activeLast7Days ?? "—"} />
        <StatCard icon={BrainCircuit} label="Assessments" value={stats.totalAssessments ?? "—"} />
        <StatCard icon={Database} label="With retakes" value={stats.participantsWithRetakes ?? "—"} />
      </div>

      <div
        className="mt-6 rounded-[26px] p-4 forr-surface sm:p-5"
        style={{ border: "1px solid var(--forr-line-soft)" }}
      >
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_auto]">
          <label className="relative block">
            <Search
              size={17}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 forr-text-mute"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, email, or profile"
              className="w-full rounded-2xl border border-black/10 bg-white py-3 pl-11 pr-4 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            />
          </label>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
            className="rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm forr-text-ink"
            aria-label="Sort participants"
          >
            {sortOptions.map(([value, label]) => (
              <option key={value} value={value}>
                Sort by {label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setDirection((value) => (value === "asc" ? "desc" : "asc"))}
            className="inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium forr-btn-secondary"
          >
            {direction === "asc" ? <ArrowDownAZ size={17} /> : <ArrowUpAZ size={17} />}
            {direction === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-5 rounded-xl bg-red-50 p-3 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}

      <div
        className="mt-5 overflow-hidden rounded-[26px] forr-surface"
        style={{ border: "1px solid var(--forr-line-soft)" }}
      >
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: "var(--forr-line-soft)" }}>
          <div className="text-sm font-medium forr-text-ink">
            {loading ? "Loading participants…" : `${result?.total ?? 0} participant account${result?.total === 1 ? "" : "s"}`}
          </div>
          {loading && <LoaderCircle size={17} className="animate-spin forr-text-teal-deep" />}
        </div>

        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[980px] border-collapse text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] forr-text-mute">
                {[
                  "Participant",
                  "Email",
                  "Date added",
                  "Last active",
                  "Assessments",
                  "Latest assessment",
                  "Profile",
                  "",
                ].map((label) => (
                  <th key={label || "action"} className="border-b px-5 py-3 font-medium" style={{ borderColor: "var(--forr-line-soft)" }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.userId} className="border-b last:border-0" style={{ borderColor: "var(--forr-line-soft)" }}>
                  <td className="px-5 py-4">
                    <div className="font-medium forr-text-ink">
                      {participant.fullName || participant.preferredName || "Unnamed participant"}
                    </div>
                    <div className="mt-1 text-xs forr-text-mute">
                      {participant.age ? `Age ${participant.age}` : "Age not saved"}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm forr-text-soft">{participant.email}</td>
                  <td className="px-5 py-4 text-sm forr-text-soft">{formatDate(participant.accountCreatedAt)}</td>
                  <td className="px-5 py-4 text-sm forr-text-soft">{formatDate(participant.lastActiveAt, true)}</td>
                  <td className="px-5 py-4 text-sm forr-text-ink">{participant.assessmentCount}</td>
                  <td className="px-5 py-4 text-sm forr-text-soft">{formatDate(participant.latestAssessmentAt)}</td>
                  <td className="px-5 py-4 text-sm forr-text-soft">{participant.activeProfileTitle || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(participant.userId)}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium forr-btn-secondary"
                    >
                      <Eye size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="divide-y lg:hidden" style={{ borderColor: "var(--forr-line-soft)" }}>
          {participants.map((participant) => (
            <button
              type="button"
              key={participant.userId}
              onClick={() => onOpen(participant.userId)}
              className="block w-full p-5 text-left"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium forr-text-ink">
                    {participant.fullName || participant.preferredName || "Unnamed participant"}
                  </div>
                  <div className="mt-1 text-xs forr-text-soft">{participant.email}</div>
                </div>
                <span className="rounded-full px-3 py-1 text-[10px] forr-surface-tint forr-text-teal-deep">
                  {participant.assessmentCount} assessment{participant.assessmentCount === 1 ? "" : "s"}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs forr-text-mute">
                <div>Added<br /><span className="forr-text-ink">{formatDate(participant.accountCreatedAt)}</span></div>
                <div>Last active<br /><span className="forr-text-ink">{formatDate(participant.lastActiveAt)}</span></div>
                <div className="col-span-2">Profile<br /><span className="forr-text-ink">{participant.activeProfileTitle || "—"}</span></div>
              </div>
            </button>
          ))}
        </div>

        {!loading && participants.length === 0 && (
          <div className="px-6 py-14 text-center">
            <Users size={28} className="mx-auto forr-text-mute" />
            <p className="mt-4 text-sm forr-text-soft">
              No participant accounts match this search yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ScoreDelta = ({ current, previous }) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  const delta = current - previous;
  const text = Math.abs(delta) < 0.005 ? "No change" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`;
  return (
    <span
      className="mt-1 block text-[10px]"
      style={{ color: delta > 0 ? "#2f7d5a" : delta < 0 ? "#a34f4f" : "var(--forr-ink-mute)" }}
    >
      {text} from prior
    </span>
  );
};

const AssessmentCard = ({ assessment, previous, active, participantName }) => {
  const dimensions = computeDimensions(assessment.answers || {});
  const previousDimensions = previous ? computeDimensions(previous.answers || {}) : null;
  const schedule = assessment.answers?.schedule || {};
  const selectedActivities = (schedule.preferredActivities || [])
    .filter((key) => key && key !== "none")
    .map(activityLabel);
  const scheduledActivities = schedule.scheduledActivities || [];

  return (
    <article
      className="rounded-[24px] p-5 forr-surface sm:p-6"
      style={{ border: active ? "1px solid var(--forr-teal)" : "1px solid var(--forr-line-soft)" }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="forr-overline">Assessment · {formatDate(assessment.completedAt, true)}</div>
            {active && (
              <span className="rounded-full px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.12em] forr-panel-action forr-text-teal-deep">
                Active baseline
              </span>
            )}
          </div>
          <h3 className="mt-2 font-display text-2xl forr-text-ink">
            {assessment.profileTitle || assessment.profileKey || "Adaptive Profile"}
          </h3>
        </div>
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <div className="text-xs forr-text-mute">Record {assessment.id || "—"}</div>
          <AdminAssessmentPdfButton
            assessment={assessment}
            participantName={participantName}
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          ["State", dimensions.state, previousDimensions?.state],
          ["Inputs", dimensions.inputs, previousDimensions?.inputs],
          ["Capacity", dimensions.capacity, previousDimensions?.capacity],
        ].map(([label, value, prior]) => (
          <div key={label} className="rounded-2xl p-4 text-center forr-surface-tint">
            <div className="font-display text-2xl forr-text-ink">{numericScore(value)}</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.14em] forr-text-mute">{label}</div>
            {previousDimensions && <ScoreDelta current={value} previous={prior} />}
          </div>
        ))}
      </div>

      <details className="mt-5 rounded-2xl forr-canvas-warm">
        <summary className="cursor-pointer px-5 py-4 text-sm font-medium forr-text-ink">
          View all assessment responses
        </summary>
        <div className="space-y-5 px-5 pb-5">
          {SECTIONS.map((section) => (
            <div key={section.id}>
              <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
                {section.dimensionLabel || section.label}
              </div>
              <div className="mt-3 divide-y rounded-2xl bg-white/75 px-4" style={{ borderColor: "var(--forr-line-soft)" }}>
                {section.items.map((item) => (
                  <div key={item.key} className="flex items-start justify-between gap-5 py-3">
                    <div className="text-xs leading-relaxed forr-text-soft">{item.q}</div>
                    <div className="shrink-0 font-display text-lg forr-text-ink">
                      {assessment.answers?.[section.id]?.[item.key] ?? "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
              Repeated behavioural responses
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(assessment.answers?.behaviors || []).length ? (
                assessment.answers.behaviors.map((key) => (
                  <span key={key} className="rounded-full px-3 py-1.5 text-xs forr-surface-tint forr-text-ink">
                    {behaviourLabel(key)}
                  </span>
                ))
              ) : (
                <span className="text-xs forr-text-mute">No behaviours selected.</span>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-white/75 p-4">
              <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
                Selected activities
              </div>
              <p className="mt-2 text-sm leading-relaxed forr-text-soft">
                {selectedActivities.length ? selectedActivities.join(", ") : "None selected"}
              </p>
            </div>
            <div className="rounded-2xl bg-white/75 p-4">
              <div className="text-[10px] uppercase tracking-[0.16em] forr-text-teal-deep">
                Scheduled commitments
              </div>
              {scheduledActivities.length ? (
                <div className="mt-2 space-y-2">
                  {scheduledActivities.map((item) => (
                    <div key={item.id} className="text-sm leading-relaxed forr-text-soft">
                      {activityLabel(item.activity)} · {(item.days || []).join(", ")} · {item.time || "—"} · {item.duration || "60"} min
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm forr-text-mute">No scheduled commitments.</p>
              )}
            </div>
          </div>
        </div>
      </details>
    </article>
  );
};

const ParticipantDetail = ({ userId, onBack }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getAdminParticipant(userId)
      .then((value) => {
        if (!cancelled) setResult(value);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) return <LoadingPanel label="Loading participant profile" />;
  if (error || !result) {
    return (
      <div className="py-12">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm forr-text-teal-deep">
          <ChevronLeft size={16} /> Back to participant directory
        </button>
        <p className="mt-6 rounded-xl bg-red-50 p-4 text-sm text-red-800">{error || "Participant profile could not be loaded."}</p>
      </div>
    );
  }

  const data = result.snapshot?.data || {};
  const identity = data.identity || result.participant || {};
  const assessments = [...(data.assessments || [])].sort((a, b) => assessmentTime(b) - assessmentTime(a));
  const oldestFirst = [...assessments].reverse();
  const priorById = new Map();
  oldestFirst.forEach((assessment, index) => {
    priorById.set(assessment.id, oldestFirst[index - 1] || null);
  });
  const checkIns = [...(data.dailyLog || [])].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const calendarEvents = [...(data.calendarEvents || [])].sort((a, b) => {
    const first = new Date(`${a.date || "1970-01-01"}T${a.time || "00:00"}`).getTime();
    const second = new Date(`${b.date || "1970-01-01"}T${b.time || "00:00"}`).getTime();
    return second - first;
  });
  const conversation = data.companionConversation || {};
  const participantName =
    identity.fullName ||
    [identity.firstName, identity.lastName].filter(Boolean).join(" ") ||
    identity.preferredName ||
    "Unnamed participant";

  return (
    <div>
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep">
        <ChevronLeft size={16} /> Back to participant directory
      </button>

      <div className="mt-6 grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.7fr)]">
        <div
          className="rounded-[28px] p-6 forr-panel-action sm:p-8"
          style={{ border: "1px solid var(--forr-action-border)" }}
        >
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] bg-white">
              <UserRound size={25} className="forr-text-teal-deep" />
            </div>
            <div>
              <div className="forr-overline">Participant profile</div>
              <h1 className="mt-2 font-display text-4xl tracking-[-0.035em] forr-text-ink">
                {participantName}
              </h1>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm forr-text-soft">
                <span className="inline-flex items-center gap-2"><Mail size={14} /> {result.account?.email || identity.email || "—"}</span>
                <span>Age {identity.age || "—"}</span>
                <span>{sexLabel(identity.sex)}</span>
              </div>
            </div>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-white/70 p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] forr-text-mute">Active profile</div>
              <div className="mt-2 text-sm font-medium forr-text-ink">{result.participant?.activeProfileTitle || "—"}</div>
            </div>
            <div className="rounded-2xl bg-white/70 p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] forr-text-mute">Account created</div>
              <div className="mt-2 text-sm font-medium forr-text-ink">{formatDate(result.account?.createdAt)}</div>
            </div>
            <div className="rounded-2xl bg-white/70 p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] forr-text-mute">Last cloud update</div>
              <div className="mt-2 text-sm font-medium forr-text-ink">{formatDate(result.snapshot?.updatedAt, true)}</div>
            </div>
          </div>
        </div>

        <div
          className="rounded-[28px] p-6 forr-surface"
          style={{ border: "1px solid var(--forr-line-soft)" }}
        >
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Read-only record</div>
              <div className="mt-1 text-sm font-medium forr-text-ink">Admin access is audited</div>
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed forr-text-soft">
            This page can review participant-submitted information but cannot alter an assessment, check-in, score, or calendar entry.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-2xl p-3 forr-surface-tint">
              <div className="font-display text-2xl forr-text-ink">{assessments.length}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">Assessments</div>
            </div>
            <div className="rounded-2xl p-3 forr-surface-tint">
              <div className="font-display text-2xl forr-text-ink">{checkIns.length}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">Check-ins</div>
            </div>
            <div className="rounded-2xl p-3 forr-surface-tint">
              <div className="font-display text-2xl forr-text-ink">{calendarEvents.length}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">Calendar events</div>
            </div>
            <div className="rounded-2xl p-3 forr-surface-tint">
              <div className="font-display text-2xl forr-text-ink">{conversation.messageCount || 0}</div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.13em] forr-text-mute">Companion messages</div>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-8">
        <div className="forr-overline">Assessment history</div>
        <h2 className="mt-2 font-display text-3xl forr-text-ink">Every saved baseline</h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed forr-text-soft">
          Retakes are stored as separate records. Score changes compare each assessment with the immediately previous baseline. Each saved baseline can be downloaded as its own assessment PDF.
        </p>
        <div className="mt-5 space-y-4">
          {assessments.map((assessment) => (
            <AssessmentCard
              key={assessment.id || assessment.completedAt}
              assessment={assessment}
              previous={priorById.get(assessment.id)}
              active={String(data.activeAssessmentId) === String(assessment.id)}
              participantName={participantName}
            />
          ))}
          {!assessments.length && (
            <div className="rounded-2xl p-6 text-sm forr-surface forr-text-mute">No completed assessments are stored.</div>
          )}
        </div>
      </section>

      <div className="mt-8 grid gap-5 xl:grid-cols-2">
        <section
          className="rounded-[26px] p-5 forr-surface sm:p-6"
          style={{ border: "1px solid var(--forr-line-soft)" }}
        >
          <div className="flex items-center gap-3">
            <Activity size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">Current-state history</div>
              <h2 className="mt-1 font-display text-2xl forr-text-ink">Recent check-ins</h2>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {checkIns.slice(0, 30).map((entry) => (
              <div key={entry.id} className="rounded-2xl p-4 forr-surface-tint">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium forr-text-ink">
                      {(entry.reportedStates || []).join(" + ") || entry.system || "State check-in"}
                    </div>
                    <div className="mt-1 text-xs forr-text-mute">{formatDate(entry.createdAt, true)}</div>
                  </div>
                  <span className="rounded-full bg-white/70 px-3 py-1 text-[10px] forr-text-teal-deep">
                    {entry.completed ? "Completed" : "Not completed"}
                  </span>
                </div>
                <div className="mt-3 text-xs leading-relaxed forr-text-soft">
                  {entry.recommendation?.title || entry.physiologicalTarget || "No recommendation title"}
                </div>
                {entry.completed && (
                  <div className="mt-2 text-xs forr-text-ink">
                    Response: {entry.feltWorse ? "Felt worse" : responseLabel(entry.improvementRating)}
                    {Number(entry.adaptivePoints) > 0 ? ` · +${entry.adaptivePoints} adaptive points` : ""}
                  </div>
                )}
              </div>
            ))}
            {!checkIns.length && <p className="text-sm forr-text-mute">No app check-ins are stored.</p>}
          </div>
        </section>

        <section
          className="rounded-[26px] p-5 forr-surface sm:p-6"
          style={{ border: "1px solid var(--forr-line-soft)" }}
        >
          <div className="flex items-center gap-3">
            <CalendarDays size={20} className="forr-text-teal-deep" />
            <div>
              <div className="forr-overline">APOS calendar</div>
              <h2 className="mt-1 font-display text-2xl forr-text-ink">Saved activities</h2>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {calendarEvents.slice(0, 30).map((event) => (
              <div key={event.id} className="rounded-2xl p-4 forr-surface-tint">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium forr-text-ink">{event.title || "Scheduled activity"}</div>
                    <div className="mt-1 text-xs forr-text-mute">
                      {formatDate(event.date)} · {event.time || "Time not set"} · {event.durationMinutes || event.duration || "—"} min
                    </div>
                  </div>
                  <span className="rounded-full bg-white/70 px-3 py-1 text-[10px] forr-text-teal-deep">
                    {event.category || event.source || "activity"}
                  </span>
                </div>
                {event.description && <p className="mt-3 text-xs leading-relaxed forr-text-soft">{event.description}</p>}
              </div>
            ))}
            {!calendarEvents.length && <p className="text-sm forr-text-mute">No calendar events are stored.</p>}
          </div>
        </section>
      </div>

      <section
        className="mt-6 rounded-[26px] p-5 forr-surface sm:p-6"
        style={{ border: "1px solid var(--forr-line-soft)" }}
      >
        <div className="flex items-center gap-3">
          <Database size={20} className="forr-text-teal-deep" />
          <div>
            <div className="forr-overline">Account metadata</div>
            <h2 className="mt-1 font-display text-2xl forr-text-ink">Storage and activity summary</h2>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.13em] forr-text-mute">Account ID</div>
            <div className="mt-2 break-all text-xs forr-text-ink">{result.account?.userId || "—"}</div>
          </div>
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.13em] forr-text-mute">Cloud revision</div>
            <div className="mt-2 text-sm forr-text-ink">{result.snapshot?.revision ?? "—"}</div>
          </div>
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.13em] forr-text-mute">Latest Companion activity</div>
            <div className="mt-2 text-sm forr-text-ink">{formatDate(conversation.lastMessageAt, true)}</div>
          </div>
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.13em] forr-text-mute">Routine settings</div>
            <div className="mt-2 text-sm forr-text-ink">{data.routineSettings ? "Saved" : "Not configured"}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cloud = useCloudAccount();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState("");

  const adminPathname = normalizeAdminPathname(location.pathname);
  const participantMatch = adminPathname.match(/^\/admin\/participants\/([^/]+)$/);
  const participantId = participantMatch ? decodeURIComponent(participantMatch[1]) : null;
  const intelligenceRoute = adminPathname === "/admin/intelligence";
  const quickAssessmentRoute = adminPathname === "/admin/quick-assessment-results";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "APOS Admin · Private Dashboard";
    let robots = document.querySelector('meta[name="robots"][data-apos-admin="true"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      robots.content = "noindex,nofollow,noarchive";
      robots.dataset.aposAdmin = "true";
      document.head.appendChild(robots);
    }
    return () => {
      document.title = previousTitle;
      robots?.remove();
    };
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setSession(await getAdminSession());
    } catch (requestError) {
      setError(requestError.message);
      setSession(requestError.body || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const activate = async () => {
    setActivating(true);
    setError("");
    try {
      await claimAdminOwner();
      await loadSession();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setActivating(false);
    }
  };

  const authorized = Boolean(session?.admin?.authorized);
  const ownerQuickResultsAuthorized = canOpenQuickAssessmentResults(session);

  return (
    <div
      className="min-h-screen forr-text-ink"
      style={{
        background:
          "linear-gradient(180deg, #F3F7F5 0%, #F8F7F2 46%, #F4F1E9 100%)",
      }}
    >
      <AdminHeader account={cloud.account} onSignOut={cloud.signOut} />
      <main className="mx-auto max-w-[1240px] px-4 py-7 sm:px-7 sm:py-10">
        {loading ? (
          <LoadingPanel />
        ) : !authorized ? (
          <AccessPanel
            session={session}
            busy={activating}
            error={error}
            onActivate={activate}
          />
        ) : quickAssessmentRoute && !ownerQuickResultsAuthorized ? (
          <div className="mx-auto max-w-2xl py-16 text-center">
            <LockKeyhole size={30} className="mx-auto forr-text-mute" />
            <h1 className="mt-5 font-display text-4xl forr-text-ink">
              Owner access is required.
            </h1>
            <p className="mt-4 text-sm leading-relaxed forr-text-soft">
              Quick assessment results is a private APOS owner testing tool.
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mt-6 rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary"
            >
              Return to APOS Admin
            </button>
          </div>
        ) : quickAssessmentRoute ? (
          <QuickAssessmentResults onBack={() => navigate("/admin")} />
        ) : intelligenceRoute && intelligenceLabEnabled ? (
          <IntelligenceLab
            agentEnabled={intelligenceAgentEnabled}
            onBack={() => navigate("/admin")}
          />
        ) : intelligenceRoute ? (
          <div className="mx-auto max-w-2xl py-16 text-center">
            <LockKeyhole size={30} className="mx-auto forr-text-mute" />
            <h1 className="mt-5 font-display text-4xl forr-text-ink">Intelligence Lab is disabled.</h1>
            <p className="mt-4 text-sm leading-relaxed forr-text-soft">
              This private preview is controlled by the APOS Intelligence Lab feature flag.
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mt-6 rounded-full px-5 py-3 text-sm font-medium forr-btn-secondary"
            >
              Return to APOS Admin
            </button>
          </div>
        ) : participantId ? (
          <ParticipantDetail userId={participantId} onBack={() => navigate("/admin")} />
        ) : (
          <Directory
            onOpen={(userId) => navigate(`/admin/participants/${encodeURIComponent(userId)}`)}
            onOpenIntelligence={() => navigate("/admin/intelligence")}
            onOpenQuickResults={
              ownerQuickResultsAuthorized
                ? () => navigate("/admin/quick-assessment-results")
                : null
            }
          />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
